import type { EChartsCoreOption } from 'echarts/core';
import type { DashboardCountTrendPoint, DashboardSalesTrendPoint } from './dashboard-api';

const TEXT = '#aeb6d9';
const GRID = 'rgba(101, 216, 255, 0.13)';
const CYAN = '#65d8ff';
const PINK = '#f019b7';
const GREEN = '#55efad';

function dateLabel(value: string | number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${String(value)}T00:00:00.000Z`));
}

function compactNumber(value: string | number): string {
  return new Intl.NumberFormat('en-US', {
    notation: Number(value) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(Number(value));
}

function compactUsdMinor(value: string | number): string {
  const dollars = Number(value) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: dollars >= 1000 ? 'compact' : 'standard',
    minimumFractionDigits: 0,
    maximumFractionDigits: dollars < 10 ? 2 : 1,
  }).format(dollars);
}

function baseOption(dates: string[], wallboard: boolean): EChartsCoreOption {
  return {
    animation: !wallboard,
    animationDuration: wallboard ? 0 : 500,
    animationDurationUpdate: wallboard ? 0 : 350,
    aria: { enabled: true },
    grid: { left: wallboard ? 72 : 58, right: 18, top: 32, bottom: 44 },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(5, 9, 32, 0.96)',
      borderColor: 'rgba(101, 216, 255, 0.45)',
      textStyle: { color: '#fff' },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { color: TEXT, formatter: dateLabel, interval: wallboard ? 4 : 6 },
      axisLine: { lineStyle: { color: GRID } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: TEXT, formatter: compactNumber },
      splitLine: { lineStyle: { color: GRID } },
    },
  };
}

export function sevenDaySalesAverage(points: readonly DashboardSalesTrendPoint[]): number[] {
  return points.map((_, index) => {
    const values = points.slice(Math.max(0, index - 6), index + 1);
    return Math.round(values.reduce((sum, point) => sum + point.amountMinor, 0) / values.length);
  });
}

export function salesChartOption(
  points: readonly DashboardSalesTrendPoint[],
  wallboard: boolean,
): EChartsCoreOption {
  const option = baseOption(
    points.map((point) => point.date),
    wallboard,
  );
  return {
    ...option,
    legend: {
      top: 0,
      right: 8,
      textStyle: { color: TEXT },
      data: ['Daily sales', '7-day average'],
    },
    yAxis: {
      type: 'value',
      minInterval: 100,
      axisLabel: { color: TEXT, formatter: compactUsdMinor },
      splitLine: { lineStyle: { color: GRID } },
    },
    tooltip: { ...(option['tooltip'] as object), valueFormatter: compactUsdMinor },
    series: [
      {
        name: 'Daily sales',
        type: 'bar',
        data: points.map((point) => point.amountMinor),
        itemStyle: { color: PINK, borderRadius: [3, 3, 0, 0] },
        emphasis: { focus: 'series' },
      },
      {
        name: '7-day average',
        type: 'line',
        data: sevenDaySalesAverage(points),
        smooth: true,
        showSymbol: false,
        lineStyle: { color: CYAN, width: 3 },
      },
    ],
  };
}

export function countChartOption(
  points: readonly DashboardCountTrendPoint[],
  kind: 'listings' | 'captures',
  wallboard: boolean,
): EChartsCoreOption {
  const option = baseOption(
    points.map((point) => point.date),
    wallboard,
  );
  const values = points.map((point) => point.count);
  return {
    ...option,
    tooltip: { ...(option['tooltip'] as object), valueFormatter: compactNumber },
    series:
      kind === 'listings'
        ? [
            {
              name: 'Cards listed',
              type: 'bar',
              data: values,
              itemStyle: { color: CYAN, borderRadius: [3, 3, 0, 0] },
              emphasis: { focus: 'series' },
            },
          ]
        : [
            {
              name: 'Card captures',
              type: 'line',
              data: values,
              smooth: true,
              showSymbol: false,
              lineStyle: { color: GREEN, width: 3 },
              areaStyle: { color: 'rgba(85, 239, 173, 0.18)' },
            },
          ],
  };
}
